import { motion } from 'framer-motion';
import { debug } from '../utils/debug';

interface LoadingSkeletonProps {
  className?: string;
  height?: string;
  width?: string;
  rounded?: boolean;
}

export const LoadingSkeleton = ({ 
  className = '', 
  height = 'h-4', 
  width = 'w-full',
  rounded = true 
}: LoadingSkeletonProps) => (
  <motion.div
    className={`bg-gray-200 ${height} ${width} ${rounded ? 'rounded' : ''} ${className}`}
    animate={{
      opacity: [0.5, 0.8, 0.5],
    }}
    transition={{
      duration: 1.5,
      repeat: Infinity,
      ease: "easeInOut"
    }}
  />
);

export const CruiseCardSkeleton = () => (
  <div className="bg-white rounded-lg shadow-md overflow-hidden">
    <LoadingSkeleton height="h-40" width="w-full" rounded={false} />
    <div className="p-4 space-y-3">
      <LoadingSkeleton height="h-6" width="w-3/4" />
      <LoadingSkeleton height="h-4" width="w-1/2" />
      <div className="flex justify-between items-center pt-2">
        <LoadingSkeleton height="h-4" width="w-1/3" />
        <LoadingSkeleton height="h-4" width="w-1/4" />
      </div>
    </div>
  </div>
);

export const StatCardSkeleton = () => (
  <div className="bg-white rounded-lg shadow-md p-6">
    <div className="flex items-center mb-4">
      <LoadingSkeleton height="h-6" width="w-6" className="mr-2" />
      <LoadingSkeleton height="h-5" width="w-1/2" />
    </div>
    <LoadingSkeleton height="h-8" width="w-1/3" className="mb-1" />
    <LoadingSkeleton height="h-4" width="w-2/3" />
  </div>
);

export const CruiseDetailsSkeleton = () => (
  <div className="space-y-6">
    {/* Header skeleton */}
    <div className="flex items-center justify-between">
      <div className="flex items-center">
        <LoadingSkeleton height="h-10" width="w-10" className="mr-4 rounded-full" />
        <div>
          <LoadingSkeleton height="h-8" width="w-48" className="mb-2" />
          <LoadingSkeleton height="h-5" width="w-32" />
        </div>
      </div>
      <div className="flex space-x-3">
        <LoadingSkeleton height="h-10" width="w-20" />
        <LoadingSkeleton height="h-10" width="w-20" />
      </div>
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <div className="lg:col-span-2 space-y-6">
        {/* Main cruise card skeleton */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <LoadingSkeleton height="h-48" width="w-full" rounded={false} />
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex items-start space-x-3">
                  <LoadingSkeleton height="h-5" width="w-5" className="mt-0.5" />
                  <div className="flex-1">
                    <LoadingSkeleton height="h-5" width="w-1/2" className="mb-1" />
                    <LoadingSkeleton height="h-4" width="w-3/4" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Itinerary skeleton */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <LoadingSkeleton height="h-6" width="w-32" className="mb-4" />
          <div className="space-y-6">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="relative pl-8">
                <LoadingSkeleton height="h-6" width="w-6" className="absolute top-1 left-0 rounded-full" />
                <LoadingSkeleton height="h-5" width="w-2/3" className="mb-2" />
                <LoadingSkeleton height="h-4" width="w-1/2" />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {/* Sidebar skeletons */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <LoadingSkeleton height="h-6" width="w-32" className="mb-4" />
          <div className="space-y-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex justify-between items-center py-2">
                <LoadingSkeleton height="h-4" width="w-1/3" />
                <LoadingSkeleton height="h-4" width="w-1/4" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);

export const HighlightsSkeleton = () => (
  <div className="space-y-8">
    {/* Header skeleton */}
    <div>
      <LoadingSkeleton height="h-8" width="w-48" className="mb-2" />
      <LoadingSkeleton height="h-5" width="w-64" />
    </div>

    {/* Stats grid skeleton */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {Array.from({ length: 4 }).map((_, i) => (
        <StatCardSkeleton key={i} />
      ))}
    </div>

    {/* Additional sections skeleton */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {Array.from({ length: 2 }).map((_, i) => (
        <div key={i} className="bg-white rounded-lg shadow-md p-6">
          <LoadingSkeleton height="h-6" width="w-40" className="mb-4" />
          <div className="space-y-4">
            {Array.from({ length: 2 }).map((_, j) => (
              <div key={j} className="flex justify-between items-center">
                <LoadingSkeleton height="h-4" width="w-1/2" />
                <LoadingSkeleton height="h-6" width="w-1/4" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const DashboardSkeleton = () => {
  debug.log('[DashboardSkeleton] Rendering DashboardSkeleton component');
  return (
  <div className="space-y-8">
    {/* Header skeleton */}
    <div className="flex flex-col md:flex-row justify-between items-center gap-4">
      <div className="w-full md:w-auto">
        <LoadingSkeleton height="h-8" width="w-48" className="mb-2" />
        <LoadingSkeleton height="h-5" width="w-64" />
      </div>
      <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
        <LoadingSkeleton height="h-10" width="w-full md:w-96" />
        <LoadingSkeleton height="h-10" width="w-32" />
      </div>
    </div>

    {/* Upcoming cruises section */}
    <div>
      <LoadingSkeleton height="h-7" width="w-48" className="mb-4" />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-6">
        {Array.from({ length: 4 }).map((_, i) => (
          <CruiseCardSkeleton key={i} />
        ))}
      </div>
    </div>

    {/* Calendar section skeleton */}
    <div className="bg-white rounded-lg shadow-md p-6">
      <LoadingSkeleton height="h-6" width="w-40" className="mb-4" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <LoadingSkeleton height="h-64" width="w-full" />
        <div className="space-y-4">
          <LoadingSkeleton height="h-32" width="w-full" />
          <LoadingSkeleton height="h-24" width="w-full" />
        </div>
      </div>
    </div>
  </div>
  );
};

export const FormSkeleton = () => (
  <div className="max-w-3xl mx-auto">
    {/* Header skeleton */}
    <div className="mb-6 flex items-center">
      <LoadingSkeleton height="h-10" width="w-10" className="mr-4 rounded-full" />
      <div>
        <LoadingSkeleton height="h-8" width="w-48" className="mb-2" />
        <LoadingSkeleton height="h-5" width="w-64" />
      </div>
    </div>

    {/* Info banner skeleton */}
    <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 rounded-r-lg">
      <LoadingSkeleton height="h-4" width="w-80" />
    </div>

    {/* Form skeleton */}
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i}>
            <LoadingSkeleton height="h-4" width="w-24" className="mb-2" />
            <LoadingSkeleton height="h-10" width="w-full" />
          </div>
        ))}
      </div>

      {/* Checkbox section skeleton */}
      <div className="mt-6">
        <div className="flex items-center mb-4">
          <LoadingSkeleton height="h-4" width="w-4" className="mr-2" />
          <LoadingSkeleton height="h-4" width="w-64" />
        </div>
      </div>

      {/* Cost section skeleton */}
      <div className="border-t border-gray-200 pt-6 mt-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i}>
              <LoadingSkeleton height="h-4" width="w-32" className="mb-2" />
              <LoadingSkeleton height="h-10" width="w-full" />
            </div>
          ))}
        </div>
      </div>

      {/* Notes section skeleton */}
      <div className="mt-6">
        <LoadingSkeleton height="h-4" width="w-16" className="mb-2" />
        <LoadingSkeleton height="h-20" width="w-full" />
      </div>

      {/* Action buttons skeleton */}
      <div className="mt-8 flex justify-end space-x-4">
        <LoadingSkeleton height="h-10" width="w-20" />
        <LoadingSkeleton height="h-10" width="w-32" />
      </div>
    </div>
  </div>
);

export const UserProfileSkeleton = () => (
  <div className="max-w-2xl mx-auto space-y-8">
    {/* Header skeleton */}
    <div className="text-center">
      <LoadingSkeleton height="h-10" width="w-10" className="mx-auto mb-4 rounded-full" />
      <LoadingSkeleton height="h-8" width="w-48" className="mx-auto mb-2" />
      <LoadingSkeleton height="h-5" width="w-64" className="mx-auto" />
    </div>

    {/* Profile form skeleton */}
    <div className="bg-white rounded-lg shadow-md p-6">
      <LoadingSkeleton height="h-6" width="w-32" className="mb-6" />
      
      <div className="space-y-6">
        {/* Name fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i}>
              <LoadingSkeleton height="h-4" width="w-24" className="mb-2" />
              <LoadingSkeleton height="h-10" width="w-full" />
            </div>
          ))}
        </div>

        {/* Loyalty programs section */}
        <div>
          <LoadingSkeleton height="h-5" width="w-40" className="mb-4" />
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 border border-gray-200 rounded-lg">
                <LoadingSkeleton height="h-10" width="w-full" />
                <LoadingSkeleton height="h-10" width="w-full" />
                <div className="flex items-center space-x-2">
                  <LoadingSkeleton height="h-10" width="w-full" />
                  <LoadingSkeleton height="h-10" width="w-10" />
                </div>
              </div>
            ))}
          </div>
          <LoadingSkeleton height="h-10" width="w-32" className="mt-4" />
        </div>

        {/* Email section */}
        <div>
          <LoadingSkeleton height="h-5" width="w-32" className="mb-4" />
          <LoadingSkeleton height="h-10" width="w-full" className="mb-2" />
          <LoadingSkeleton height="h-4" width="w-64" />
        </div>

        {/* Password section */}
        <div>
          <LoadingSkeleton height="h-5" width="w-48" className="mb-4" />
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i}>
                <LoadingSkeleton height="h-4" width="w-32" className="mb-2" />
                <LoadingSkeleton height="h-10" width="w-full" />
              </div>
            ))}
          </div>
          <LoadingSkeleton height="h-10" width="w-32" className="mt-4" />
        </div>

        {/* Save button */}
        <div className="flex justify-end pt-6">
          <LoadingSkeleton height="h-10" width="w-32" />
        </div>
      </div>
    </div>
  </div>
);

export const PostCruiseDetailsSkeleton = () => (
  <div className="max-w-2xl mx-auto space-y-6">
    {/* Header skeleton */}
    <div className="flex items-center">
      <LoadingSkeleton height="h-10" width="w-10" className="mr-4 rounded-full" />
      <div>
        <LoadingSkeleton height="h-8" width="w-48" className="mb-2" />
        <LoadingSkeleton height="h-5" width="w-64" />
      </div>
    </div>

    {/* Info banner skeleton */}
    <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
      <LoadingSkeleton height="h-4" width="w-80" />
    </div>

    {/* Cruise summary card skeleton */}
    <div className="bg-white rounded-lg shadow-md p-6">
      <LoadingSkeleton height="h-6" width="w-40" className="mb-4" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="flex justify-between">
            <LoadingSkeleton height="h-4" width="w-24" />
            <LoadingSkeleton height="h-4" width="w-32" />
          </div>
        ))}
      </div>
    </div>

    {/* Form sections skeleton */}
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="space-y-8">
        {/* Expenses section */}
        <div>
          <LoadingSkeleton height="h-6" width="w-32" className="mb-4" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i}>
                <LoadingSkeleton height="h-4" width="w-24" className="mb-2" />
                <LoadingSkeleton height="h-10" width="w-full" />
              </div>
            ))}
          </div>
        </div>

        {/* Gambling section */}
        <div>
          <LoadingSkeleton height="h-6" width="w-32" className="mb-4" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i}>
                <LoadingSkeleton height="h-4" width="w-24" className="mb-2" />
                <LoadingSkeleton height="h-10" width="w-full" />
              </div>
            ))}
          </div>
        </div>

        {/* Notes section */}
        <div>
          <LoadingSkeleton height="h-4" width="w-16" className="mb-2" />
          <LoadingSkeleton height="h-20" width="w-full" />
        </div>

        {/* Action buttons */}
        <div className="flex justify-end space-x-4">
          <LoadingSkeleton height="h-10" width="w-20" />
          <LoadingSkeleton height="h-10" width="w-32" />
        </div>
      </div>
    </div>
  </div>
);

export const LoginSkeleton = () => (
  <div className="flex relative overflow-hidden">
    {/* Background Image */}
    <div 
      className="absolute inset-0 z-0"
      style={{
        backgroundImage: 'url(/images/photo-3601425.jpeg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        filter: 'brightness(0.7)'
      }}
    />

    {/* Content Container */}
    <div className="flex-1 flex pt-16 relative z-10">
      {/* Left Side - Features (hidden on mobile) */}
      <div className="hidden lg:flex flex-col justify-center px-12 w-2/3">
        <LoadingSkeleton height="h-12" width="w-80" className="mb-6" />
        <LoadingSkeleton height="h-6" width="w-96" className="mb-12" />

        <div className="grid grid-cols-2 gap-8">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-white/10 rounded-lg p-6">
              <LoadingSkeleton height="h-8" width="w-8" className="mb-4" />
              <LoadingSkeleton height="h-6" width="w-32" className="mb-2" />
              <LoadingSkeleton height="h-4" width="w-full" />
            </div>
          ))}
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/3 flex items-center justify-center p-8">
        <div className="bg-white/10 rounded-lg p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <LoadingSkeleton height="h-20" width="w-20" className="mx-auto mb-4 rounded-full" />
            <LoadingSkeleton height="h-7" width="w-48" className="mx-auto mb-2" />
            <LoadingSkeleton height="h-5" width="w-64" className="mx-auto" />
          </div>

          <div className="space-y-6">
            {/* Email field */}
            <div>
              <LoadingSkeleton height="h-4" width="w-16" className="mb-1" />
              <LoadingSkeleton height="h-10" width="w-full" />
            </div>

            {/* Password field */}
            <div>
              <LoadingSkeleton height="h-4" width="w-20" className="mb-1" />
              <LoadingSkeleton height="h-10" width="w-full" />
            </div>

            {/* Forgot password link */}
            <div className="flex justify-end">
              <LoadingSkeleton height="h-4" width="w-32" />
            </div>

            {/* Login button */}
            <LoadingSkeleton height="h-10" width="w-full" />
          </div>

          {/* Sign up link */}
          <div className="mt-6 text-center">
            <LoadingSkeleton height="h-4" width="w-48" className="mx-auto" />
          </div>

          {/* Terms links */}
          <div className="mt-4 text-center">
            <LoadingSkeleton height="h-4" width="w-64" className="mx-auto" />
          </div>
        </div>
      </div>
    </div>
  </div>
);

export const SignupSkeleton = () => (
  <div className="flex relative overflow-hidden">
    {/* Background Image */}
    <div 
      className="absolute inset-0 z-0"
      style={{
        backgroundImage: 'url(/images/photo-3601425.jpeg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        filter: 'brightness(0.7)'
      }}
    />

    {/* Content Container */}
    <div className="flex-1 flex pt-16 relative z-10">
      {/* Left Side - Features (hidden on mobile) */}
      <div className="hidden lg:flex flex-col justify-center px-12 w-2/3">
        <LoadingSkeleton height="h-12" width="w-80" className="mb-6" />
        <LoadingSkeleton height="h-6" width="w-96" className="mb-4" />
        
        {/* Beta banner skeleton */}
        <div className="bg-white/10 rounded-lg p-4 mb-8">
          <div className="flex items-center mb-2">
            <LoadingSkeleton height="h-5" width="w-5" className="mr-2" />
            <LoadingSkeleton height="h-5" width="w-24" />
          </div>
          <LoadingSkeleton height="h-4" width="w-full" />
        </div>

        <div className="grid grid-cols-2 gap-8">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-white/10 rounded-lg p-6">
              <LoadingSkeleton height="h-8" width="w-8" className="mb-4" />
              <LoadingSkeleton height="h-6" width="w-32" className="mb-2" />
              <LoadingSkeleton height="h-4" width="w-full" />
            </div>
          ))}
        </div>
      </div>

      {/* Right Side - Signup Form */}
      <div className="w-full lg:w-1/3 flex items-center justify-center p-8">
        <div className="bg-white/10 rounded-lg p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <LoadingSkeleton height="h-20" width="w-20" className="mx-auto mb-4 rounded-full" />
            <LoadingSkeleton height="h-7" width="w-48" className="mx-auto mb-2" />
            <LoadingSkeleton height="h-5" width="w-64" className="mx-auto" />
          </div>

          <div className="space-y-6">
            {/* Invite code field (conditional) */}
            <div>
              <LoadingSkeleton height="h-4" width="w-24" className="mb-1" />
              <LoadingSkeleton height="h-10" width="w-full" />
            </div>

            {/* Name field */}
            <div>
              <LoadingSkeleton height="h-4" width="w-20" className="mb-1" />
              <LoadingSkeleton height="h-10" width="w-full" />
            </div>

            {/* Email field */}
            <div>
              <LoadingSkeleton height="h-4" width="w-16" className="mb-1" />
              <LoadingSkeleton height="h-10" width="w-full" />
            </div>

            {/* Password field */}
            <div>
              <LoadingSkeleton height="h-4" width="w-20" className="mb-1" />
              <LoadingSkeleton height="h-10" width="w-full" />
            </div>

            {/* Confirm password field */}
            <div>
              <LoadingSkeleton height="h-4" width="w-32" className="mb-1" />
              <LoadingSkeleton height="h-10" width="w-full" />
            </div>

            {/* Signup button */}
            <LoadingSkeleton height="h-10" width="w-full" />
          </div>

          {/* Login link */}
          <div className="mt-6 text-center">
            <LoadingSkeleton height="h-4" width="w-48" className="mx-auto" />
          </div>

          {/* Terms links */}
          <div className="mt-4 text-center">
            <LoadingSkeleton height="h-4" width="w-64" className="mx-auto" />
          </div>
        </div>
      </div>
    </div>
  </div>
);

export const VerifyEmailSkeleton = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
    <div className="max-w-md w-full space-y-8">
      <div className="text-center">
        <LoadingSkeleton height="h-16" width="w-16" className="mx-auto mb-6 rounded-full" />
        <LoadingSkeleton height="h-8" width="w-48" className="mx-auto mb-4" />
        <LoadingSkeleton height="h-4" width="w-80" className="mx-auto" />
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 space-y-4">
        <LoadingSkeleton height="h-4" width="w-full" className="mb-4" />
        <LoadingSkeleton height="h-4" width="w-3/4" />
        
        <div className="space-y-3 pt-4">
          <LoadingSkeleton height="h-10" width="w-full" />
          <LoadingSkeleton height="h-10" width="w-full" />
          <LoadingSkeleton height="h-10" width="w-full" />
        </div>
      </div>

      <div className="text-center">
        <LoadingSkeleton height="h-4" width="w-32" className="mx-auto" />
      </div>
    </div>
  </div>
);

export const ResetPasswordSkeleton = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
    <div className="max-w-md w-full space-y-8">
      <div className="text-center">
        <LoadingSkeleton height="h-20" width="w-20" className="mx-auto mb-6 rounded-full" />
        <LoadingSkeleton height="h-8" width="w-40" className="mx-auto mb-4" />
        <LoadingSkeleton height="h-4" width="w-96" className="mx-auto" />
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="space-y-6">
          <div>
            <LoadingSkeleton height="h-4" width="w-24" className="mb-2" />
            <LoadingSkeleton height="h-10" width="w-full" />
          </div>
          
          <LoadingSkeleton height="h-10" width="w-full" />
        </div>
        
        <div className="mt-4 text-center">
          <LoadingSkeleton height="h-4" width="w-24" className="mx-auto" />
        </div>
      </div>
    </div>
  </div>
);

export const HelpPageSkeleton = () => (
  <div className="max-w-6xl mx-auto space-y-12">
    {/* Header skeleton */}
    <div className="text-center">
      <LoadingSkeleton height="h-12" width="w-12" className="mx-auto mb-4 rounded-full" />
      <LoadingSkeleton height="h-10" width="w-64" className="mx-auto mb-4" />
      <LoadingSkeleton height="h-6" width="w-96" className="mx-auto" />
    </div>

    {/* Getting Started section skeleton */}
    <div className="bg-white rounded-lg shadow-md p-8">
      <div className="flex items-center mb-6">
        <LoadingSkeleton height="h-6" width="w-6" className="mr-3" />
        <LoadingSkeleton height="h-7" width="w-40" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex items-start space-x-3">
              <LoadingSkeleton height="h-8" width="w-8" className="rounded-full" />
              <div className="flex-1">
                <LoadingSkeleton height="h-5" width="w-3/4" className="mb-2" />
                <LoadingSkeleton height="h-4" width="w-full" />
              </div>
            </div>
          ))}
        </div>
        <div className="bg-blue-50 rounded-lg p-6">
          <LoadingSkeleton height="h-5" width="w-32" className="mb-4" />
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-center space-x-3">
                <LoadingSkeleton height="h-5" width="w-5" />
                <LoadingSkeleton height="h-4" width="w-40" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>

    {/* Features section skeleton */}
    <div className="bg-white rounded-lg shadow-md p-8">
      <div className="flex items-center mb-6">
        <LoadingSkeleton height="h-6" width="w-6" className="mr-3" />
        <LoadingSkeleton height="h-7" width="w-56" />
      </div>
      <LoadingSkeleton height="h-4" width="w-full" className="mb-6" />
      
      {/* New features grid */}
      <div className="mb-8">
        <LoadingSkeleton height="h-6" width="w-48" className="mb-4" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="p-6 rounded-lg border-2 border-gray-200">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center">
                  <LoadingSkeleton height="h-8" width="w-8" className="mr-3 rounded-lg" />
                  <LoadingSkeleton height="h-5" width="w-32" />
                </div>
                <LoadingSkeleton height="h-5" width="w-16" className="rounded-full" />
              </div>
              <LoadingSkeleton height="h-4" width="w-full" />
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* FAQ section skeleton */}
    <div className="bg-white rounded-lg shadow-md p-8">
      <div className="flex items-center mb-6">
        <LoadingSkeleton height="h-6" width="w-6" className="mr-3" />
        <LoadingSkeleton height="h-7" width="w-64" />
      </div>
      
      {/* Category filter skeleton */}
      <div className="mb-6">
        <div className="flex flex-wrap gap-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <LoadingSkeleton key={i} height="h-8" width="w-24" className="rounded-full" />
          ))}
        </div>
      </div>

      {/* FAQ items skeleton */}
      <div className="space-y-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <LoadingSkeleton height="h-5" width="w-3/4" />
              <LoadingSkeleton height="h-5" width="w-5" />
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Contact form skeleton */}
    <div className="bg-white rounded-lg shadow-md p-8">
      <div className="flex items-center mb-6">
        <LoadingSkeleton height="h-6" width="w-6" className="mr-3" />
        <LoadingSkeleton height="h-7" width="w-40" />
      </div>
      <LoadingSkeleton height="h-4" width="w-full" className="mb-6" />
      
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i}>
              <LoadingSkeleton height="h-4" width="w-24" className="mb-2" />
              <LoadingSkeleton height="h-10" width="w-full" />
            </div>
          ))}
        </div>
        <div>
          <LoadingSkeleton height="h-4" width="w-20" className="mb-2" />
          <LoadingSkeleton height="h-24" width="w-full" />
        </div>
        <div className="flex justify-end">
          <LoadingSkeleton height="h-10" width="w-32" />
        </div>
      </div>
    </div>
  </div>
);

// Generic page skeleton for Android loading states
export const GenericPageSkeleton = () => {
  debug.log('[GenericPageSkeleton] Rendering GenericPageSkeleton component');
  return (
  <div className="space-y-6">
    {/* Header skeleton */}
    <div className="flex flex-col md:flex-row justify-between items-center gap-4">
      <div className="w-full md:w-auto">
        <LoadingSkeleton height="h-8" width="w-48" className="mb-2" />
        <LoadingSkeleton height="h-5" width="w-64" />
      </div>
      <div className="flex gap-4 w-full md:w-auto">
        <LoadingSkeleton height="h-10" width="w-32" />
        <LoadingSkeleton height="h-10" width="w-32" />
      </div>
    </div>

    {/* Content skeleton */}
    <div className="bg-white rounded-lg shadow-md p-6">
      <LoadingSkeleton height="h-6" width="w-40" className="mb-4" />
      <div className="space-y-4">
        <LoadingSkeleton height="h-4" width="w-full" />
        <LoadingSkeleton height="h-4" width="w-5/6" />
        <LoadingSkeleton height="h-4" width="w-4/6" />
        <LoadingSkeleton height="h-32" width="w-full" className="mt-6" />
      </div>
    </div>

    {/* Additional content blocks */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <LoadingSkeleton height="h-6" width="w-32" className="mb-4" />
        <div className="space-y-3">
          <LoadingSkeleton height="h-4" width="w-full" />
          <LoadingSkeleton height="h-4" width="w-3/4" />
          <LoadingSkeleton height="h-4" width="w-1/2" />
        </div>
      </div>
      <div className="bg-white rounded-lg shadow-md p-6">
        <LoadingSkeleton height="h-6" width="w-32" className="mb-4" />
        <div className="space-y-3">
          <LoadingSkeleton height="h-4" width="w-full" />
          <LoadingSkeleton height="h-4" width="w-3/4" />
          <LoadingSkeleton height="h-4" width="w-1/2" />
        </div>
      </div>
    </div>
  </div>
  );
};

export const CollectionSkeleton = () => (
  <div className="space-y-8">
    {/* Header skeleton */}
    <div>
      <LoadingSkeleton height="h-9" width="w-64" className="mb-2" />
      <LoadingSkeleton height="h-5" width="w-96" />
    </div>

    {/* Overall statistics skeleton */}
    <div className="bg-gradient-to-br from-teal-900 to-teal-700 rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <LoadingSkeleton height="h-6" width="w-48" className="mb-2 bg-teal-800" />
          <LoadingSkeleton height="h-4" width="w-32" className="bg-teal-800" />
        </div>
        <LoadingSkeleton height="h-12" width="w-12" className="rounded-full bg-secondary-800" />
      </div>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <LoadingSkeleton height="h-4" width="w-32" className="bg-teal-800" />
          <LoadingSkeleton height="h-8" width="w-24" className="bg-teal-800" />
        </div>
        <LoadingSkeleton height="h-3" width="w-full" className="bg-teal-800 rounded-full" />
        <LoadingSkeleton height="h-4" width="w-20" className="bg-teal-800 ml-auto" />
      </div>
    </div>

    {/* Cruise line cards skeleton */}
    <div className="space-y-4">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4 flex-1">
              <LoadingSkeleton height="h-8" width="w-8" className="rounded" />
              <div className="flex-1">
                <LoadingSkeleton height="h-6" width="w-48" className="mb-2" />
                <div className="flex items-center space-x-4">
                  <LoadingSkeleton height="h-8" width="w-20" />
                  <LoadingSkeleton height="h-4" width="w-24" />
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <LoadingSkeleton height="h-2" width="w-32" className="rounded-full" />
              <LoadingSkeleton height="h-4" width="w-12" />
              <LoadingSkeleton height="h-5" width="w-5" className="rounded" />
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const ShipDetailsSkeleton = () => (
  <div className="space-y-6">
    {/* Header skeleton */}
    <div className="flex items-center">
      <LoadingSkeleton height="h-10" width="w-10" className="mr-4 rounded-full" />
      <div>
        <LoadingSkeleton height="h-9" width="w-64" className="mb-2" />
        <LoadingSkeleton height="h-5" width="w-48" />
      </div>
    </div>

    {/* Main ship card skeleton */}
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <LoadingSkeleton height="h-64" width="w-full" rounded={false} />
      <div className="p-6 space-y-6">
        {/* Ship Information section */}
        <div>
          <LoadingSkeleton height="h-6" width="w-40" className="mb-4" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="flex items-start space-x-3">
                <LoadingSkeleton height="h-5" width="w-5" className="mt-0.5" />
                <div className="flex-1">
                  <LoadingSkeleton height="h-5" width="w-24" className="mb-1" />
                  <LoadingSkeleton height="h-4" width="w-32" />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Specifications section */}
        <div className="pt-6 border-t border-gray-200">
          <LoadingSkeleton height="h-6" width="w-40" className="mb-4" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-start space-x-3">
                <LoadingSkeleton height="h-5" width="w-5" className="mt-0.5" />
                <div className="flex-1">
                  <LoadingSkeleton height="h-5" width="w-32" className="mb-1" />
                  <LoadingSkeleton height="h-4" width="w-24" />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Capacity section */}
        <div className="pt-6 border-t border-gray-200">
          <LoadingSkeleton height="h-6" width="w-32" className="mb-4" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-start space-x-3">
                <LoadingSkeleton height="h-5" width="w-5" className="mt-0.5" />
                <div className="flex-1">
                  <LoadingSkeleton height="h-5" width="w-24" className="mb-1" />
                  <LoadingSkeleton height="h-4" width="w-20" />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* External Actions section */}
        <div className="pt-6 border-t border-gray-200">
          <LoadingSkeleton height="h-6" width="w-40" className="mb-3" />
          <div className="flex gap-3">
            <LoadingSkeleton height="h-10" width="w-32" />
            <LoadingSkeleton height="h-10" width="w-36" />
          </div>
        </div>
      </div>
    </div>
  </div>
);

export const FriendsSkeleton = () => (
  <div className="space-y-8">
    {/* Header skeleton */}
    <div className="flex items-center justify-between">
      <div>
        <div className="flex items-center gap-3 mb-2">
          <LoadingSkeleton height="h-8" width="w-8" className="rounded" />
          <LoadingSkeleton height="h-9" width="w-32" />
        </div>
        <LoadingSkeleton height="h-4" width="w-64" />
      </div>
      <LoadingSkeleton height="h-10" width="w-32" className="rounded-lg" />
    </div>

    {/* Tabs skeleton */}
    <div className="bg-white rounded-lg shadow-md">
      <div className="border-b border-gray-200">
        <div className="flex -mb-px">
          <LoadingSkeleton height="h-14" width="w-1/2" className="rounded-t-lg" />
          <LoadingSkeleton height="h-14" width="w-1/2" className="rounded-t-lg" />
        </div>
      </div>
      <div className="p-6">
        {/* Tab content skeleton - Feed view with two-column layout */}
        <div className="space-y-4">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="flex flex-col lg:flex-row lg:items-stretch">
                {/* Image skeleton - Left column */}
                <div className="h-40 lg:h-auto lg:w-[30rem] xl:w-[34rem] lg:flex-shrink-0 bg-gray-200"></div>
                {/* Details skeleton - Right column */}
                <div className="p-4 flex-1 space-y-4">
                  <div className="space-y-2">
                    <LoadingSkeleton height="h-6" width="w-48" />
                    <LoadingSkeleton height="h-4" width="w-32" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <LoadingSkeleton height="h-5" width="w-6" className="rounded" />
                      <LoadingSkeleton height="h-4" width="w-32" />
                      <LoadingSkeleton height="h-3" width="w-24" />
                    </div>
                    <div className="space-y-1">
                      <LoadingSkeleton height="h-5" width="w-6" className="rounded" />
                      <LoadingSkeleton height="h-4" width="w-40" />
                      <LoadingSkeleton height="h-3" width="w-32" />
                    </div>
                  </div>
                  <LoadingSkeleton height="h-4" width="w-48" />
                  <div className="space-y-2">
                    <LoadingSkeleton height="h-3" width="w-24" />
                    <div className="flex flex-wrap gap-2">
                      {Array.from({ length: 3 }).map((_, j) => (
                        <LoadingSkeleton key={j} height="h-6" width="w-20" className="rounded" />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

export const FriendCruisesSkeleton = () => (
  <div className="space-y-8">
    {/* Header skeleton */}
    <div>
      <LoadingSkeleton height="h-5" width="w-32" className="mb-4" />
      <div>
        <div className="flex items-center gap-3 mb-2">
          <LoadingSkeleton height="h-8" width="w-8" className="rounded" />
          <LoadingSkeleton height="h-9" width="w-48" />
        </div>
        <LoadingSkeleton height="h-4" width="w-56" />
      </div>
    </div>

    {/* Tabs skeleton */}
    <div className="bg-white rounded-lg shadow-md">
      <div className="border-b border-gray-200">
        <div className="flex -mb-px">
          <LoadingSkeleton height="h-14" width="w-1/2" className="rounded-t-lg" />
          <LoadingSkeleton height="h-14" width="w-1/2" className="rounded-t-lg" />
        </div>
      </div>
      <div className="p-6">
        {/* List View skeleton - Two column layout */}
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="flex flex-col lg:flex-row lg:items-stretch">
                {/* Image skeleton - Left column */}
                <div className="h-40 lg:h-auto lg:w-[30rem] xl:w-[34rem] lg:flex-shrink-0 bg-gray-200"></div>
                {/* Details skeleton - Right column */}
                <div className="p-4 flex-1 space-y-4">
                  <div className="space-y-2">
                    <LoadingSkeleton height="h-6" width="w-48" />
                    <LoadingSkeleton height="h-4" width="w-32" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <LoadingSkeleton height="h-5" width="w-6" className="rounded" />
                      <LoadingSkeleton height="h-4" width="w-32" />
                      <LoadingSkeleton height="h-3" width="w-24" />
                    </div>
                    <div className="space-y-1">
                      <LoadingSkeleton height="h-5" width="w-6" className="rounded" />
                      <LoadingSkeleton height="h-4" width="w-40" />
                      <LoadingSkeleton height="h-3" width="w-32" />
                    </div>
                  </div>
                  <LoadingSkeleton height="h-4" width="w-48" />
                  <div className="space-y-2">
                    <LoadingSkeleton height="h-3" width="w-24" />
                    <div className="flex flex-wrap gap-2">
                      {Array.from({ length: 4 }).map((_, j) => (
                        <LoadingSkeleton key={j} height="h-6" width="w-24" className="rounded" />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);